import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sparkles, Wrench, Gauge, Package } from 'lucide-react'
import { api } from '../lib/api'
import { Card } from '../components/ui/Card'
import { AntetFix } from '../components/ui/AntetFix'

/**
 * „Ce s-a schimbat" — cuprinsul livrărilor din CRM.
 *
 * Victor: „crezi că poți să afișezi în CRM un cuprins al modificărilor live pe care ți le face?
 * Să pot să urmăresc și eu ce se mai face nou, pe ce se mai face update".
 *
 * Textele vin din mesajele de commit, nu dintr-o listă ținută de mână. O listă separată s-ar
 * desincroniza de la prima livrare făcută în grabă, și atunci n-ar mai fi de încredere — adică
 * n-ar mai fi citită.
 *
 * Fiecare intrare are titlul și **de ce** s-a făcut. Un „am reparat atașamentele" nu spune
 * nimic; „fiecare alegere de fișiere o înlocuia pe cea dinainte" spune și ce să reîncerci.
 */
type Autor = 'Alexandru' | 'Victor' | 'Madalin' | 'altcineva'

interface Schimbare {
  hash: string
  data: string
  fel: 'nou' | 'reparat' | 'imbunatatit' | 'altele'
  zona: string | null
  titlu: string
  explicatie: string
  autor: Autor
}

/**
 * Cine a scris modificarea.
 *
 * Culori diferite, nu doar nume: la derulare rapida se vede din priviri a cui e o intrare, fara
 * sa fie citita eticheta.
 */
const STIL_AUTOR: Record<Autor, string> = {
  Alexandru: 'bg-b4l-blue/10 text-b4l-blue',
  Victor: 'bg-purple-100 text-purple-700',
  Madalin: 'bg-amber-100 text-amber-800',
  altcineva: 'bg-b4l-bg text-b4l-text-muted',
}

const STIL = {
  nou: { eticheta: 'Nou', icon: Sparkles, clasa: 'bg-b4l-green-light text-b4l-green-dark' },
  reparat: { eticheta: 'Reparat', icon: Wrench, clasa: 'bg-b4l-amber-light text-b4l-amber-dark' },
  imbunatatit: {
    eticheta: 'Îmbunătățit',
    icon: Gauge,
    clasa: 'bg-b4l-blue-light text-b4l-blue-dark',
  },
  altele: { eticheta: 'Întreținere', icon: Package, clasa: 'bg-b4l-bg text-b4l-text-muted' },
} as const

const ZI = { weekday: 'long', day: 'numeric', month: 'long' } as const

function ziua(iso: string): string {
  return new Date(iso).toLocaleDateString('ro-RO', ZI)
}

function ora(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
}

/**
 * Cand a fost gata: ziua si ora, pe acelasi rand cu schimbarea.
 *
 * Solicitare: „adauga si ora si minut pentru finalizare implementare, atat la Sinteza cat si la
 * Jurnal". In Jurnal ora exista deja, dar fara zi — iar un rand citit fara antetul de deasupra
 * lui nu spunea cand. Acum fiecare rand se tine singur.
 *
 * E ora la care s-a scris schimbarea, nu a vreunui raport de dupa: se ia direct din istoricul
 * codului, ca tot restul paginii.
 */
/** Doar ziua si luna — pentru antetul cardului, unde ora n-ar spune nimic. */
function zi(iso: string): string {
  return new Date(iso).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' })
}

function dataOra(iso: string): string {
  return `${zi(iso)} · ${ora(iso)}`
}

/** Numele zonelor, cum le spune lumea. Scope-ul din commit e prescurtat pentru cod. */
const ZONE: Record<string, string> = {
  taskuri: 'Task-uri',
  sampling: 'Sampling',
  inbox: 'Inbox',
  email: 'Email',
  alerte: 'Alerte',
  produse: 'Catalog produse',
  utilizatori: 'Utilizatori',
  changelog: 'Ce s-a schimbat',
  comenzi: 'Comenzi',
  emag: 'eMAG',
}

export function CeSaSchimbatPage() {
  const [tab, setTab] = useState<'sinteza' | 'jurnal'>('sinteza')
  const [doarNoutati, setDoarNoutati] = useState(false)
  /*
    De la septembrie 2026 la cod lucreaza doua persoane. Alexandru: „ar trebui sa vedem separat
    aici modificarile noastre, de ale lui" (18.09.2026).
  */
  const [autor, setAutor] = useState<'toti' | Autor>('toti')

  const q = useQuery<{ schimbari: Schimbare[]; deLa: string }>({
    queryKey: ['changelog'],
    queryFn: async () => (await api.get('/changelog')).data,
  })

  /** Grupate pe zile: „ce s-a făcut azi" e întrebarea, nu „a 47-a livrare". */
  const peZile = useMemo(() => {
    const lista = (q.data?.schimbari ?? [])
      /*
        Jurnalul nu se povestește pe sine.

        Intrările despre pagina asta — cum grupează, cum anonimizează, cum scapă barele oblice
        din regex — sunt despre unealtă, nu despre CRM. Cine deschide jurnalul vrea să afle ce
        s-a schimbat în munca lui, nu cum e făcut ecranul pe care se uită.

        În Sinteză rămân: acolo e un rând cu titlul, care spune că pagina există și atât.
      */
      .filter((s) => s.zona !== 'changelog')
      /*
        „Doar schimbări vizibile": raman `nou` si `reparat`, dispar `imbunatatit` si `altele`.

        Adica exact ce se poate observa deschizand CRM-ul. Restul — cod rescris, unelte,
        documentatie — e munca reala, dar despre care nimeni nu-si da seama uitandu-se la ecran.

        Eticheta a fost intai „Doar ce se vede" si a trebuit explicata (Alexandru, 18.09.2026):
        daca un nume de casuta cere o explicatie, numele e de vina, nu omul.
      */
      .filter((s) => (doarNoutati ? s.fel === 'nou' || s.fel === 'reparat' : true))
      .filter((s) => (autor === 'toti' ? true : s.autor === autor))
    const map = new Map<string, Schimbare[]>()
    for (const s of lista) {
      const cheie = s.data.slice(0, 10)
      const g = map.get(cheie)
      if (g) g.push(s)
      else map.set(cheie, [s])
    }
    return [...map.entries()]
  }, [q.data, doarNoutati, autor])

  const total = q.data?.schimbari.length ?? 0

  /**
   * Sinteza: ce s-a adaugat, pe zone.
   *
   * Alexandru: „pentru Victor, hai sa adaugam un tab cu o sinteza, cu ce s-a adaugat". Jurnalul
   * raspunde la „ce s-a facut azi"; sinteza raspunde la „ce stie CRM-ul sa faca acum si nu stia
   * saptamana trecuta". Sunt doua intrebari, si a doua nu se poate citi dintr-o lista de 33 de
   * intrari puse cronologic.
   *
   * Se arata doar titlurile, fara explicatii: o sinteza cu paragrafe nu mai e sinteza.
   */
  const sinteza = useMemo(() => {
    // Pagina nu se raportează pe sine, nici aici. Ce s-a schimbat la ecranul ăsta se vede
    // deschizându-l, nu citind despre el.
    const lista = (q.data?.schimbari ?? []).filter(
      (s) => s.zona !== 'changelog' && (s.fel === 'nou' || s.fel === 'reparat'),
    )
    const map = new Map<string, { toate: Schimbare[]; nou: number; reparat: number }>()
    for (const s of lista) {
      const cheie = s.zona ?? 'altele'
      let g = map.get(cheie)
      if (!g) {
        g = { toate: [], nou: 0, reparat: 0 }
        map.set(cheie, g)
      }
      g.toate.push(s)
      if (s.fel === 'nou') g.nou++
      else g.reparat++
    }
    /*
      O SINGURA lista, cronologica, nu doua stivuite.

      Cardul avea intai noutatile, apoi reparatiile, fiecare descrescatoare in sine. Puse una sub
      alta ieseau 06.09, 09.09, 06.09 — adica pareau nesortate, si asa au si fost citite. Ce
      distinge o noutate de o reparatie e iconita de pe rand, nu pozitia in card; numaratoarea din
      antet ramane pentru cine vrea proportia.
    */
    for (const g of map.values()) g.toate.sort((a, b) => (a.data < b.data ? 1 : -1))
    /*
      Zonele cele mai RECENTE sus, nu cele mai voluminoase.

      Sortarea dupa volum punea Sampling primul permanent — are cat toate celelalte la un loc —
      iar capul paginii arata o data veche de doua saptamani. Pagina parea ramasa in urma tocmai
      cand nu era: lucrul proaspat statea in carduri mici, jos. Intrebarea „de ce ultima
      actualizare e din 30 august?" a venit exact din asta.

      Data ultimei schimbari se scrie si in antetul fiecarui card, ca raspunsul la „s-a mai
      lucrat aici?" sa nu ceara derulare.
    */
    return [...map.entries()].sort((a, b) => (a[1].toate[0].data < b[1].toate[0].data ? 1 : -1))
  }, [q.data])

  return (
    <div className="max-w-3xl space-y-4">
      <AntetFix>
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold">Ce s-a schimbat</h1>
            <p className="mt-0.5 text-sm text-b4l-text-muted">
              Tot ce a intrat în CRM de la 20 august încoace — {total} livrări. Se citește direct
              din istoricul codului, deci nu poate rămâne în urmă.
            </p>
          </div>
          {tab === 'jurnal' && (
            <div className="flex flex-wrap items-center gap-3 pb-1">
              <div className="flex gap-1">
                {(['toti', 'Alexandru', 'Victor', 'Madalin'] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAutor(a)}
                    className={`rounded-full px-2.5 py-0.5 text-xs ${
                      autor === a
                        ? 'bg-b4l-text text-white'
                        : 'bg-b4l-bg text-b4l-text-muted hover:bg-b4l-bg-soft'
                    }`}
                  >
                    {a === 'toti' ? 'Toți' : a}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={doarNoutati}
                  onChange={(e) => setDoarNoutati(e.target.checked)}
                />
                Doar schimbări vizibile
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-1 border-b border-b4l-border">
          {(
            [
              ['sinteza', 'Sinteză'],
              ['jurnal', 'Jurnal'],
            ] as const
          ).map(([cheie, eticheta]) => (
            <button
              key={cheie}
              onClick={() => setTab(cheie)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
                tab === cheie
                  ? 'border-b4l-green font-medium text-b4l-green-dark'
                  : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
              }`}
            >
              {eticheta}
            </button>
          ))}
        </div>
      </AntetFix>

      {q.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
      {q.isError && <p className="text-sm text-b4l-red">Nu am putut citi istoricul livrărilor.</p>}

      {tab === 'sinteza' && (
        <div className="space-y-3">
          {sinteza.map(([zona, g]) => (
            <Card key={zona}>
              <div className="p-3">
                <h2 className="mb-2 text-sm font-semibold text-b4l-text">
                  {ZONE[zona] ?? zona}
                  <span className="ml-2 text-[11px] font-normal text-b4l-text-muted">
                    {g.nou > 0 && `${g.nou} ${g.nou === 1 ? 'noutate' : 'noutăți'}`}
                    {g.nou > 0 && g.reparat > 0 && ' · '}
                    {g.reparat > 0 && `${g.reparat} ${g.reparat === 1 ? 'reparație' : 'reparații'}`}
                  </span>
                  <span className="ml-2 text-[11px] font-normal tabular-nums text-b4l-text-dim">
                    ultima: {zi(g.toate[0].data)}
                  </span>
                </h2>
                <ul className="space-y-1">
                  {g.toate.map((s) => (
                    <li
                      key={s.hash}
                      className={`flex gap-2 text-sm ${s.fel === 'nou' ? '' : 'text-b4l-text-muted'}`}
                    >
                      {s.fel === 'nou' ? (
                        <Sparkles size={13} className="mt-0.5 shrink-0 text-b4l-green" />
                      ) : (
                        <Wrench size={13} className="mt-0.5 shrink-0 text-b4l-amber" />
                      )}
                      <span>{s.titlu}</span>
                      <span className="ml-auto shrink-0 whitespace-nowrap pt-0.5 text-[11px] tabular-nums text-b4l-text-dim">
                        {dataOra(s.data)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
          {sinteza.length === 0 && !q.isLoading && (
            <p className="text-sm text-b4l-text-muted">Nimic de arătat încă.</p>
          )}
        </div>
      )}

      {tab === 'jurnal' &&
        peZile.map(([zi, schimbari]) => (
          <div key={zi}>
            <h2 className="mb-1.5 text-sm font-semibold capitalize text-b4l-text">
              {ziua(schimbari[0].data)}
            </h2>
            <Card>
              <div className="divide-y divide-b4l-border/60">
                {schimbari.map((s) => {
                  const stil = STIL[s.fel]
                  const Icon = stil.icon
                  return (
                    <div key={s.hash} className="p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${stil.clasa}`}
                        >
                          <Icon size={11} />
                          {stil.eticheta}
                        </span>
                        {s.zona && (
                          <span className="rounded bg-b4l-bg px-1.5 py-0.5 text-[11px] text-b4l-text-muted">
                            {s.zona}
                          </span>
                        )}
                        <span
                          className={`rounded px-1.5 py-0.5 text-[11px] ${STIL_AUTOR[s.autor]}`}
                          title="Cine a scris modificarea"
                        >
                          {s.autor}
                        </span>
                        <span className="ml-auto whitespace-nowrap text-[11px] tabular-nums text-b4l-text-dim">
                          {dataOra(s.data)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-medium text-b4l-text">{s.titlu}</p>
                      {s.explicatie && (
                        <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-b4l-text-muted">
                          {s.explicatie}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </Card>
          </div>
        ))}

      {tab === 'jurnal' && !q.isLoading && peZile.length === 0 && (
        <p className="text-sm text-b4l-text-muted">Nimic de arătat încă.</p>
      )}
    </div>
  )
}
